/** Symbology v2's legend, read straight out of the one table that also
 * paints the map — src/map/tables/symbology-table.json, emitted by
 * tools/symbology/emit_table.py and byte-identical to the copy the iOS
 * bundle carries.
 *
 * Why this file exists at all: before v2, five of the nine legend swatches
 * on the phone painted a colour the map does not draw (MVUM trail, dirt
 * road, paved road, highway and contours were all hand-typed
 * approximations), the web planner's ten rows were a third hand-typed set,
 * and none of the three followed the Map markings theme. The fix is not a
 * better copy pass — it is that no swatch has a hex of its own any more.
 * `swatchFor` returns what the style's own class table says the current
 * ground is painted with, casing, dash and z14 width included; a swatch can
 * only be wrong now if the map is wrong with it.
 *
 * Pure and node-safe — JSON in, plain objects out, no DOM and no maplibre —
 * so tests/symbology-table.test.ts can check the whole legend without a
 * browser, the same contract map/firstlight.ts's buildStyle keeps. */

import TABLE from "./tables/symbology-table.json";
import TEMPLATE from "./template.json";
import NIGHT_MAP from "./tables/night-map.json";
import NIGHT_LAYER_OVERRIDES from "./tables/night-layer-overrides.json";
import TOPO_PAINT from "./tables/dustlark-topo-paint.json";
import type { MapLook, SymbologyGroupId, ToggleKey } from "./firstlight";

/** The four painted grounds the class table carries a colour column for.
 * NOT the same axis as MapLook: six looks collapse onto four grounds,
 * because what a line has to survive is the paper/photograph underneath
 * it, not which raster produced it. */
export type Ground = "paper" | "topo" | "imagery" | "night";

/** Mirrors buildStyle's own branch exactly (firstlight.ts): night is
 * applied only on the two looks that are NOT their own paper — the topo
 * sheets and the photograph keep daylight, because the whole point of
 * those grounds is that they ARE the sheet. Read it as: whatever ground
 * the map is about to paint on, ask the table for that column. */
export function groundForLook(look: MapLook, night: boolean): Ground {
  if (look === "imagery") return "imagery";
  if (look === "dustlarkTopo" || look === "ustopo" || look === "historic") return "topo";
  return night ? "night" : "paper";
}

// --------------------------------------------------------------- shapes

type GroundMap<T> = Record<Ground, T>;

/** Single-tone rim (paper, topo, night) or the imagery two-tone: a dark
 * core with a light sleeve outside it, so a coloured dash survives both
 * dry grass and snow in the same frame. */
export type CasingSpec =
  | { hex: string; rimPt: number }
  | { core: string; coreRimPt: number; sleeve: string; sleeveRimPt: number }
  | null;

export interface SymbologyClass {
  id: string;
  label: string;
  blurb: string;
  group: SymbologyGroupId;
  toggle: ToggleKey;
  layers: string[];
  legendOrder: number;
  fill: GroundMap<string>;
  casing: GroundMap<CasingSpec>;
  dash: number[] | null;
  cap: string;
  widthZ14: number;
  minzoom: number;
  opacity: number;
}

export interface SymbologyOverprint {
  id: string;
  label: string;
  blurb: string;
  toggle: ToggleKey;
  layers: string[];
  legendOrder: number;
  color: GroundMap<string>;
  dash: number[] | null;
  cap: string;
  minzoom: number;
  opacity: number;
}

export interface SymbologyReferenceLine {
  id: string;
  label: string;
  group: SymbologyGroupId;
  toggle: ToggleKey;
  layers: string[];
  color: GroundMap<string>;
  dash: number[] | null;
  widthZ14: number;
  minzoom: number;
  opacity: number;
}

export interface SymbologyPoint {
  id: string;
  family: string;
  kind: string;
  tier: string;
  toggle: ToggleKey;
  minzoom: number | null;
  defaultOn: boolean;
}

export interface LegendRef {
  kind: "class" | "overprint" | "reference" | "new";
  order: number;
  ref: string;
}

const T = TABLE as unknown as {
  classes: SymbologyClass[];
  overprints: SymbologyOverprint[];
  referenceLines: SymbologyReferenceLine[];
  points: SymbologyPoint[];
  legend: LegendRef[];
};

export const CLASSES: readonly SymbologyClass[] = T.classes;
export const OVERPRINTS: readonly SymbologyOverprint[] = T.overprints;
export const REFERENCE_LINES: readonly SymbologyReferenceLine[] = T.referenceLines;
export const POINTS: readonly SymbologyPoint[] = T.points;

/** The 27 legend entries in the spec's own order (SPEC.md section 9). */
export const LEGEND: readonly LegendRef[] = [...T.legend].sort((a, b) => a.order - b.order);

const CLASS_BY_ID = new Map(CLASSES.map((c) => [c.id, c]));
const OVERPRINT_BY_ID = new Map(OVERPRINTS.map((o) => [o.id, o]));
const REFERENCE_BY_ID = new Map(REFERENCE_LINES.map((r) => [r.id, r]));
const POINT_BY_ID = new Map(POINTS.map((p) => [p.id, p]));

// -------------------------------------------------------------- swatches

export type Swatch =
  | {
      kind: "line";
      color: string;
      casing: CasingSpec;
      dash: number[] | null;
      width: number;
      cap: string;
      opacity: number;
    }
  | { kind: "dot"; color: string; stroke: string | null }
  | { kind: "fill"; color: string };

export interface LegendRow {
  /** The legend entry's own ref id — stable, and what a test names. */
  id: string;
  kind: LegendRef["kind"];
  order: number;
  label: string;
  /** One line in the rider's terms; only the classes and overprints carry
   * one, so this is often null and the row simply has no footnote. */
  blurb: string | null;
  /** The row whose switch turns this drawing off. */
  toggle: ToggleKey;
  swatch: Swatch;
}

/** A transparent circle-color means the dot IS its ring (fords, the
 * bookable-campsite ring) — MapLibre's own spelling for it in this
 * template, which the swatch has to reproduce rather than paint a black
 * disc. */
const TRANSPARENT = "rgba(0,0,0,0)";

const TEMPLATE_LAYERS = new Map(
  (TEMPLATE as unknown as { layers: Record<string, any>[] }).layers.map((l) => [
    String(l.id),
    l,
  ]),
);

/** Point families are the one part of the table with no colour column of
 * their own: a campsite dot is one hex on every ground, because a dot with
 * a cream stroke reads the same on paper, on a sheet and on a photograph.
 * So the swatch takes the hex the TEMPLATE paints — the same value the
 * generated style will use — and puts it through the same two tables
 * applyNight/applyLook put it through, rather than inventing a second
 * palette here.
 *
 * Resolution order matches applyNight's `nightValue`: the per-layer
 * override first (`layerId|hex`), then the global day->night map, then the
 * hex unchanged. Imagery leaves point fills alone (it recolours casings and
 * widens halos), so that ground and `paper` read the same value. */
function groundHex(layerId: string, hex: string, ground: Ground): string {
  if (hex === TRANSPARENT) return hex;
  if (ground === "night") {
    const overrides = NIGHT_LAYER_OVERRIDES as Record<string, string>;
    const map = NIGHT_MAP as Record<string, string>;
    const key = hex.toLowerCase();
    return overrides[`${layerId}|${key}`] ?? map[key] ?? hex;
  }
  if (ground === "topo") {
    const paint = (TOPO_PAINT as Record<string, Record<string, unknown>>)[layerId];
    for (const k of ["circle-color", "fill-color", "line-color", "text-color"]) {
      const v = paint?.[k];
      if (typeof v === "string") return v;
    }
  }
  return hex;
}

/** The template layer a point family is drawn by: the id itself when it is
 * a circle/symbol layer, else its `-fill`/`-line` half (wilderness is a
 * fill + line + label trio whose legend ref is the bare family name). */
function pointLayer(id: string): { layerId: string; layer: Record<string, any> } | null {
  for (const candidate of [id, `${id}-fill`, `${id}-line`]) {
    const layer = TEMPLATE_LAYERS.get(candidate);
    if (layer) return { layerId: candidate, layer };
  }
  return null;
}

function pointSwatch(point: SymbologyPoint, ground: Ground): Swatch {
  const found = pointLayer(point.id);
  if (!found) return { kind: "dot", color: "currentColor", stroke: null };
  const { layerId, layer } = found;
  const paint = (layer.paint ?? {}) as Record<string, unknown>;
  const pick = (k: string) => (typeof paint[k] === "string" ? (paint[k] as string) : null);

  const fill = pick("circle-color") ?? pick("fill-color");
  const stroke = pick("circle-stroke-color");
  if (fill !== null) {
    // A transparent disc with a coloured ring is a ring, not a dot with an
    // invisible body — swap them so the swatch draws what the map draws.
    const isRing = fill === TRANSPARENT && stroke !== null;
    if (layer.type === "fill") {
      return { kind: "fill", color: groundHex(layerId, fill, ground) };
    }
    return {
      kind: "dot",
      color: isRing ? TRANSPARENT : groundHex(layerId, fill, ground),
      stroke: stroke === null ? null : groundHex(layerId, stroke, ground),
    };
  }
  const line = pick("line-color");
  if (line !== null) {
    const width = typeof paint["line-width"] === "number" ? (paint["line-width"] as number) : 1.4;
    return {
      kind: "line",
      color: groundHex(layerId, line, ground),
      casing: null,
      dash: (paint["line-dasharray"] as number[]) ?? null,
      width,
      cap: "round",
      opacity: 1,
    };
  }
  const text = pick("text-color");
  if (text !== null) {
    return { kind: "dot", color: groundHex(layerId, text, ground), stroke: null };
  }
  return { kind: "dot", color: "currentColor", stroke: null };
}

/** The label a point family shows in the legend. Points carry no label of
 * their own — the row that switches them off already has the rider's name
 * for them, and two names for one thing is how a legend starts drifting. */
function labelForToggle(toggle: ToggleKey, rows: readonly { key: ToggleKey; label: string }[]): string {
  return rows.find((r) => r.key === toggle)?.label ?? toggle;
}

/** Resolves the 27-entry legend for one ground. `rows` is firstlight's
 * SYMBOLOGY_ROWS, passed in rather than imported so this module stays free
 * of an import cycle with the toggle machinery. */
export function legendRows(
  ground: Ground,
  rows: readonly { key: ToggleKey; label: string }[],
): LegendRow[] {
  const out: LegendRow[] = [];
  for (const entry of LEGEND) {
    if (entry.kind === "class") {
      const c = CLASS_BY_ID.get(entry.ref);
      if (!c) continue;
      out.push({
        id: c.id,
        kind: entry.kind,
        order: entry.order,
        label: c.label,
        blurb: c.blurb,
        toggle: c.toggle,
        swatch: {
          kind: "line",
          color: c.fill[ground],
          casing: c.casing[ground],
          dash: c.dash,
          width: c.widthZ14,
          cap: c.cap,
          opacity: c.opacity,
        },
      });
    } else if (entry.kind === "overprint") {
      const o = OVERPRINT_BY_ID.get(entry.ref);
      if (!o) continue;
      out.push({
        id: o.id,
        kind: entry.kind,
        order: entry.order,
        label: o.label,
        blurb: o.blurb,
        toggle: o.toggle,
        swatch: {
          kind: "line",
          color: o.color[ground],
          casing: null,
          dash: o.dash,
          // An overprint is a tick pattern ON a class line, not a class of
          // its own, so the table gives it no width; 2.0 pt is the width
          // the seasonal layer hugs its host at on the commonest class.
          width: 2,
          cap: o.cap,
          opacity: o.opacity,
        },
      });
    } else if (entry.kind === "reference") {
      const r = REFERENCE_BY_ID.get(entry.ref);
      if (!r) continue;
      out.push({
        id: r.id,
        kind: entry.kind,
        order: entry.order,
        label: r.label,
        blurb: null,
        toggle: r.toggle,
        swatch: {
          kind: "line",
          color: r.color[ground],
          casing: null,
          dash: r.dash,
          width: r.widthZ14,
          cap: "round",
          opacity: r.opacity,
        },
      });
    } else {
      const p = POINT_BY_ID.get(entry.ref);
      if (!p) continue;
      out.push({
        id: p.id,
        kind: entry.kind,
        order: entry.order,
        label: labelForToggle(p.toggle, rows),
        blurb: null,
        toggle: p.toggle,
        swatch: pointSwatch(p, ground),
      });
    }
  }
  return out;
}

/** Every legend ref resolves to something — the guard the swatch code
 * above would otherwise hide by `continue`-ing past a broken entry.
 * tests/symbology-table.test.ts asserts this is empty. */
export function unresolvedLegendRefs(): LegendRef[] {
  return LEGEND.filter((e) => {
    if (e.kind === "class") return !CLASS_BY_ID.has(e.ref);
    if (e.kind === "overprint") return !OVERPRINT_BY_ID.has(e.ref);
    if (e.kind === "reference") return !REFERENCE_BY_ID.has(e.ref);
    return !POINT_BY_ID.has(e.ref);
  });
}
